import axios from 'axios';
import { InvoiceData } from '../types/invoiceTypes';
import { downloadBlob } from './invoiceApi'; // Importiere die downloadBlob Funktion

// Lese die API-URL aus Umgebungsvariablen oder nutze einen Fallback
const API_URL = process.env.REACT_APP_API_URL || 'https://invoice-service-php-685396932615.europe-west3.run.app';

/**
 * Sendet die Rechnungsdaten an das XRechnung Backend und erwartet eine XML-Datei zurück.
 * @param data Die Rechnungsdaten.
 * @param token Optional: Firebase ID Token für Authentifizierung.
 * @returns Ein Promise, das zu einem Objekt mit XML-Blob und Dateinamen auflöst.
 */
export const sendXRechnungData = async (data: InvoiceData, token?: string): Promise<{ blob: Blob; filename: string }> => {
  const xRechnungUrl = `${API_URL}/generate-xrechnung`;
  console.log('Sending XRechnung data to:', xRechnungUrl);

  const headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
    'Accept': 'application/xml',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await axios.post(xRechnungUrl, data, {
      headers: headers,
      responseType: 'blob',
      validateStatus: function (status) {
        return status === 200; // Nur 200 OK akzeptieren für Blob-Antworten
      },
    });

    if (!response.data || !(response.data instanceof Blob)) {
      console.error('Invalid response data: expected Blob, got:', typeof response.data);
      throw new Error('Invalid response format: expected XML blob');
    }

    console.log('Received valid blob with size:', response.data.size);

    // Dateinamen aus Content-Disposition Header extrahieren (robuste Methode)
    let filename = 'XRechnung.xml'; // Fallback
    
    // Axios konvertiert Header-Namen zu Kleinbuchstaben
    const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
    
    if (contentDisposition) {
      // Robuste Regex für verschiedene Formate:
      // attachment; filename="XRechnung_Re2025-453.xml"
      // attachment; filename=XRechnung_Re2025-453.xml (ohne Anführungszeichen)
      // attachment; filename*=UTF-8''XRechnung_Re2025-453.xml
      const filenameMatch = contentDisposition.match(/filename[*]?=["']?([^"';]+)["']?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    return { blob: response.data, filename };
  } catch (error: any) {
    console.error('Error in sendXRechnungData:', error);
    
    // Spezielle Behandlung für HTTP-Fehler (4xx, 5xx)
    if (error.response) {
      const statusCode = error.response.status;
      let errorMessage = `Request failed with status code ${statusCode}`;
      
      // Bei JSON-Fehlerantworten (wenn der Browser sie automatisch geparst hat)
      if (error.response.data) {
        try {
          // Wenn die Daten ein Blob sind
          if (error.response.data instanceof Blob) {
            const jsonText = await error.response.data.text();
            const jsonError = JSON.parse(jsonText);
            errorMessage = jsonError.error || jsonError.message || errorMessage;
          } 
          // Wenn die Daten bereits ein Objekt sind
          else if (typeof error.response.data === 'object') {
            errorMessage = error.response.data.error || error.response.data.message || errorMessage;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
      }
      
      const enhancedError: any = new Error(errorMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    } else if (error.request) {
      // Die Anfrage wurde gesendet, aber keine Antwort erhalten
      throw new Error('No response received from server. Please check your network connection.');
    } else {
      // Sonstige Fehler
      throw new Error(error.message || 'An unknown error occurred while sending data.');
    }
  }
};

// Utility-Funktion für XRechnung-Downloads
export const downloadXRechnungBlob = (blob: Blob, filename: string): void => {
  downloadBlob(blob, filename);
}; 